import { BrandMode } from '@/constants/branding';
import { supabase } from '@/lib/supabase';
import { logger } from '@/lib/logger';
import { handleSupabaseError } from '@/lib/supabase';

export interface LedgerBalance {
  store_id: string;
  customer_phone: string;
  balance: number;
  updated_at: string;
  customer_name: string;
  customer_name_locale?: string;
}

export const LedgerService = {
  async getBalances(storeId: string): Promise<LedgerBalance[]> {
    const { data, error } = await supabase
      .from('store_customer_balances')
      .select('store_id, customer_phone, balance, updated_at, store_customers:store_customers(customer_name, customer_name_locale)')
      .eq('store_id', storeId);
    if (error) throw error;
    return (data || []).map((row: any) => ({
      store_id: row.store_id,
      customer_phone: row.customer_phone,
      balance: row.balance,
      updated_at: row.updated_at,
      customer_name: row.store_customers?.customer_name || row.customer_phone,
      customer_name_locale: row.store_customers?.customer_name_locale,
    }));
  },

  async getDueLedgerEntriesByInvoiceId(invoiceId: string) {
    const { data, error } = await supabase
      .from('ledger_entries')
      .select('*')
      .eq('invoice_id', invoiceId)
      .eq('entry_type', 'given')
      .order('created_at', { ascending: true });
    if (error) throw error;
    return data || [];
  },

  async sendDueWhatsappUpdate({ customerName, dueAmount, storeName, customerPhone, brandMode }: {
    customerName: string,
    dueAmount: number | string,
    storeName: string,
    customerPhone: string,
    brandMode?: string
  }) {
    if (!customerPhone || !/^\d{10}$/.test(customerPhone)) return;
    const contentVariables = {
      customer_name: customerName,
      due_amount: String(dueAmount),
      store_name: storeName,
      customer_phone: customerPhone
    };
    const contentType = brandMode === BrandMode.Munshiji ? 'munshiji_dues_update' : 'due_update';
    try {
      supabase.functions.invoke('whatsapp-send', {
        body: {
          to: customerPhone,
          contentVariables,
          contentType,
        },
      });
    } catch (err) {
      // Ignore WhatsApp errors
    }
  },

  async sendPaymentWhatsappUpdate({ storeId, customerName, paymentAmount, storeName, customerPhone, brandMode }: {
    storeId: string,
    customerName: string,
    paymentAmount: number | string,
    storeName: string,
    customerPhone: string,
    brandMode?: string
  }) {
    if (!customerPhone || !/^\d{10}$/.test(customerPhone)) return;
    // Fetch latest total dues
    const totalDues = await this.getCustomerBalance(storeId, customerPhone);
    const contentVariables = {
      customer_name: customerName,
      payment_amount: String(paymentAmount),
      store_name: storeName,
      total_dues: totalDues !== null ? String(totalDues) : 'Unknown',
      customer_phone: customerPhone
    };
    const contentType = brandMode === BrandMode.Munshiji ? 'munshiji_payment_update' : 'payment_update';
    try {
      supabase.functions.invoke('whatsapp-send', {
        body: {
          to: customerPhone,
          contentVariables,
          contentType,
        },
      });
    } catch (err) {
      // Ignore WhatsApp errors
    }
  },

  async createDueEntry({ storeId, customerPhone, amount, note, invoiceId }: {
    storeId: string,
    customerPhone: string,
    amount: number,
    note?: string,
    invoiceId?: string | null
  }) {
    const { error, data } = await supabase.rpc('create_due_entry', {
      p_store_id: storeId,
      p_customer_phone: customerPhone,
      p_amount: amount,
      p_note: note || 'Manual Due',
      p_invoice_id: typeof invoiceId !== 'undefined' ? invoiceId : null,
    });
    return { error, data };
  },

  async getAllForStore(storeId: string) {
    const { data, error } = await supabase
      .from('ledger_entries')
      .select('id, amount, created_at, entry_type, customer_phone, invoice_id, note, payment_mode')
      .eq('store_id', storeId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  async getAllLedgerEntriesAdmin() {
    const { data, error } = await supabase
      .from('ledger_entries')
      .select('id, amount, created_at, entry_type, customer_phone, invoice_id, note, payment_mode')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  async getCustomerBalance(storeId: string, customerPhone: string): Promise<number | null> {
    const { data, error } = await supabase
      .from('store_customer_balances')
      .select('balance')
      .eq('store_id', storeId)
      .eq('customer_phone', customerPhone)
      .maybeSingle();
    if (error || !data) return null;
    return typeof data.balance === 'number' ? data.balance : Number(data.balance);
  },

  async getCustomerLedgerWithAllocations(storeId: string, customerPhone: string) {
    try {
      logger.info('Fetching customer ledger with allocations and invoices', { storeId, customerPhone });

      const { data, error } = await supabase
        .from('ledger_entries')
        .select(`
          *,
          payment_allocations:payment_allocations(*),
          invoice:invoices(id, bill_amount, creation_time, remaining_dues)
        `)
        .eq('store_id', storeId)
        .eq('customer_phone', customerPhone)
        .order('created_at', { ascending: false });

      if (error) {
        logger.error('Failed to fetch ledger entries with allocations and invoices', { error, storeId, customerPhone });
        throw error;
      }

      logger.debug('Ledger entries with allocations and invoices fetched successfully', {
        storeId,
        customerPhone,
        count: data?.length
      });

      return data || [];
    } catch (error) {
      logger.error('Error in getCustomerLedgerWithAllocations', { error, storeId, customerPhone });
      throw handleSupabaseError(error);
    }
  },

  async getCustomerLedgerWithAllocationsPaginated(
    storeId: string, 
    customerPhone: string, 
    page: number = 1, 
    pageSize: number = 15,
    skipCount: boolean = false
  ) {
    try {
      logger.info('Fetching customer ledger with pagination', { storeId, customerPhone, page, pageSize, skipCount });

      // Calculate offset
      const offset = (page - 1) * pageSize;

      let totalCount = 0;
      
      // Only get total count on initial load (page 1) or when explicitly requested
      if (page === 1 && !skipCount) {
        const { count, error: countError } = await supabase
          .from('ledger_entries')
          .select('*', { count: 'exact', head: true })
          .eq('store_id', storeId)
          .eq('customer_phone', customerPhone);

        if (countError) {
          logger.error('Failed to get total count', { error: countError, storeId, customerPhone });
          throw countError;
        }
        
        totalCount = count || 0;
      }

      // Get paginated data with joins
      const { data, error } = await supabase
        .from('ledger_entries')
        .select(`
          *,
          payment_allocations:payment_allocations(*),
          invoice:invoices(id, bill_amount, creation_time, remaining_dues)
        `)
        .eq('store_id', storeId)
        .eq('customer_phone', customerPhone)
        .order('created_at', { ascending: false })
        .range(offset, offset + pageSize - 1);

      if (error) {
        logger.error('Failed to fetch ledger entries with pagination', { error, storeId, customerPhone, page });
        throw error;
      }

      // For subsequent pages, we can't calculate hasMore without total count
      // So we'll use the data length to determine if there might be more
      const hasMore = page === 1 
        ? offset + pageSize < totalCount 
        : data && data.length === pageSize; // If we got a full page, there might be more

      logger.debug('Ledger entries with pagination fetched successfully', {
        storeId,
        customerPhone,
        page,
        count: data?.length,
        total: totalCount,
        hasMore
      });

      return {
        data: data || [],
        hasMore,
        total: totalCount
      };
    } catch (error) {
      logger.error('Error in getCustomerLedgerWithAllocationsPaginated', { error, storeId, customerPhone, page });
      throw handleSupabaseError(error);
    }
  },

  async handlePaymentSubmit({ 
    storeId, 
    customerPhone, 
    entryAmount, 
    entryNote, 
    balance, 
    customer, 
    store, 
    brand,
    paymentMethod = 'Cash'
  }: {
    storeId: string,
    customerPhone: string,
    entryAmount: string,
    entryNote: string,
    balance: number | null,
    customer: any,
    store: any,
    brand: any,
    paymentMethod?: string
  }) {
    const totalDues = balance ? Math.abs(balance) : 0;
    const paymentAmount = Number(entryAmount);

    if (!storeId || !customerPhone || !entryAmount || isNaN(paymentAmount) || paymentAmount <= 0) {
      return { error: 'Enter a valid amount' };
    }

    if (paymentAmount > totalDues) {
      return { error: `Payment amount cannot exceed total dues of ₹${totalDues}` };
    }

    try {
      const { data: allocations, error } = await supabase.rpc('auto_allocate_payment', {
        p_store_id: storeId,
        p_customer_phone: customerPhone,
        p_payment_amount: paymentAmount,
        p_payment_mode: paymentMethod,
        p_note: entryNote || undefined
      });

      if (error) {
        return { error: error.message };
      }

      // Send WhatsApp update
      await this.sendPaymentWhatsappUpdate({
        storeId,
        customerName: customer.customer_name,
        paymentAmount: paymentAmount,
        storeName: store.name,
        customerPhone: customer.customer_phone,
        brandMode: brand.mode,
      });

      const allocatedInvoices = (allocations || []).filter((a: any) => a.invoice_id);
      
      return { 
        success: true, 
        data: { 
          allocations, 
          allocatedInvoices,
          paymentAmount 
        } 
      };
    } catch (err: any) {
      return { error: err.message || 'Failed to create ledger entry' };
    }
  },

  async handleDuesSubmit({ 
    storeId, 
    customerPhone, 
    entryAmount, 
    entryNote, 
    customer, 
    store, 
    brand 
  }: {
    storeId: string,
    customerPhone: string,
    entryAmount: string,
    entryNote: string,
    customer: any,
    store: any,
    brand: any
  }) {
    const dueAmount = Number(entryAmount);

    if (!storeId || !customerPhone || !entryAmount || isNaN(dueAmount) || dueAmount <= 0) {
      return { error: 'Enter a valid amount' };
    }

    try {
      const { error } = await this.createDueEntry({
        storeId,
        customerPhone,
        amount: dueAmount,
        note: entryNote || 'Manual Due',
        invoiceId: null,
      });
      
      if (error) {
        return { error: error.message };
      }

      // Send WhatsApp update
      await this.sendDueWhatsappUpdate({
        customerName: customer.customer_name,
        dueAmount: dueAmount,
        storeName: store.name,
        customerPhone: customer.customer_phone,
        brandMode: brand.mode,
      });

      return { 
        success: true, 
        data: { dueAmount } 
      };
    } catch (err: any) {
      return { error: err.message || 'Failed to create ledger entry' };
    }
  },

  async sendPaymentRequestWhatsappUpdate({ customerName, requestAmount, storeName, customerPhone, brandMode, totalBalance }: {
    customerName: string,
    requestAmount: number | string,
    storeName: string,
    customerPhone: string,
    brandMode?: string,
    totalBalance?: number | string
  }) {
    if (!customerPhone || !/^\d{10}$/.test(customerPhone)) return;
    const contentVariables = {
      customer_name: customerName,
      request_amount: String(requestAmount),
      total_balance: String(totalBalance || 0),
      store_name: storeName,
      customer_phone: customerPhone
    };
    const contentType = brandMode === BrandMode.Munshiji ? 'munshiji_payment_request' : 'payment_request';
    try {
      supabase.functions.invoke('whatsapp-send', {
        body: {
          to: customerPhone,
          contentVariables,
          contentType,
        },
      });
    } catch (err) {
      // Ignore WhatsApp errors
    }
  },

  async createPaymentRequestEntry({ 
    storeId, 
    customerPhone, 
    message,
    requestedAmount,
    customerName,
    storeName,
    brandMode,
    totalBalance
  }: {
    storeId: string,
    customerPhone: string,
    message: string,
    requestedAmount: number,
    customerName: string,
    storeName: string,
    brandMode?: string,
    totalBalance?: number
  }) {
    try {
      logger.info('Creating payment request entry', { storeId, customerPhone, requestedAmount });

      const { data, error } = await supabase.rpc('create_payment_request_entry', {
        p_store_id: storeId,
        p_customer_phone: customerPhone,
        p_message: message,
        p_requested_amount: requestedAmount
      });

      if (error) {
        logger.error('Failed to create payment request entry', { error, storeId, customerPhone });
        return { error: error.message };
      }

      // Send WhatsApp update - DISABLED
      // await this.sendPaymentRequestWhatsappUpdate({
      //   customerName,
      //   requestAmount: requestedAmount,
      //   storeName,
      //   customerPhone,
      //   brandMode,
      //   totalBalance,
      // });

      logger.info('Payment request entry created successfully', { 
        storeId, 
        customerPhone, 
        entryId: data 
      });

      return { 
        success: true, 
        data: { entryId: data } 
      };
    } catch (err: any) {
      logger.error('Error in createPaymentRequestEntry', { error: err, storeId, customerPhone });
      return { error: err.message || 'Failed to create payment request entry' };
    }
  }
}; 